// web/src/tutorial/detail/string-flow.ts
// StringFlow · 软件界面详细说明（Rust 端逐个参数）
//
// 写作原则（作者要求）：按界面截图从上到下一块一块讲，逐个参数回答
// 「它管什么 / 什么时候要动它 / 动完会看到什么」，不介绍用户看不到的实现细节。
//
// 依据：H:\string_flow_rust（只读）
//   src/ui/app.rs（四个页签）、avatar_panel.rs、avatar_display.rs、avatar_manager.rs、
//   midi_panel.rs、execution_panel.rs、fingering_panel.rs、components.rs、data.rs（默认值）、
//   src/midi/midi_processor.rs 与 midi_quartet_split.rs / midi_note_methods.rs（量化、通道、输出文件名）、
//   src/recorder/bow_recorder.rs（弓速）、src/violin/violin_note.rs（音名）、
//   asset/i18n/zh-CN.json（界面原文，参数名与按钮名以此为准）

import { l } from "../l";
import type { TutorialDoc } from "../types";

export const stringFlowDetail: TutorialDoc = {
  key: "string_flow",
  slug: "string-flow",
  kind: "detail",
  icon: "🎻",
  title: l("StringFlow 软件界面详细说明", "StringFlow UI Reference"),
  subtitle: l(
    "按界面截图逐个页签讲清楚每个分区、每个按钮和全部可调参数：参数各自控制什么、什么时候需要调、调大或调小会出现什么变化。",
    "A screen-by-screen walkthrough of every tab, section, button and adjustable parameter: what it controls, when to tune it, and what changes when you do.",
  ),
  readMinutes: 12,
  video: {
    hidden: true,
    caption: l("本页不内嵌视频", "No embedded video on this page"),
  },
  images: [
    {
      src: "/assets/image/products/string-flow/CN/角色设置.png",
      srcEn: "/assets/image/products/string-flow/EN/Avatar.png",
      label: l("角色设置", "Avatar Setup"),
      width: 914,
      height: 882,
    },
    {
      src: "/assets/image/products/string-flow/CN/MIDI处理.png",
      srcEn: "/assets/image/products/string-flow/EN/MIDI.png",
      label: l("MIDI 处理", "MIDI"),
      width: 914,
      height: 882,
    },
    {
      src: "/assets/image/products/string-flow/CN/执行操作.png",
      srcEn: "/assets/image/products/string-flow/EN/Execute.png",
      label: l("执行操作", "Execute"),
      width: 914,
      height: 882,
    },
    {
      src: "/assets/image/products/string-flow/CN/指法检查.png",
      srcEn: "/assets/image/products/string-flow/EN/Fingering.png",
      label: l("指法检查", "Fingering"),
      width: 1920,
      height: 1040,
    },
  ],
  sections: [
    // ─────────────────────────────────────────────────────────────
    {
      id: "how-to-read",
      title: l("这篇怎么用", "How to use this page"),
      blocks: [
        {
          kind: "p",
          text: l(
            "本页是**参数查询手册**：按软件顶部的页签顺序，把 ⚙ 角色设置、🎵 MIDI处理、▶ 执行操作、🎼 指法检查 四屏从上到下拆开讲。每一块卡片是干什么的、每个可调参数管什么、什么时候该动它、动完会看到什么变化，都在这里。",
            "This page is a **reference**: it walks the app's four tabs — ⚙ 角色设置 / ⚙ Avatar Setup, 🎵 MIDI处理 / 🎵 MIDI, ▶ 执行操作 / ▶ Execute, 🎼 指法检查 / 🎼 Fingering — from top to bottom. What each card is for, what every adjustable parameter controls, when to touch it, and what changes when you do.",
          ),
        },
        {
          kind: "p",
          text: l(
            "只想先把流程跑通，请看[快速上手](/quickstart/string-flow.html)：那一页不调任何参数，选演奏者 → 选 MIDI → 生成动画三步走完。本页是跑通之后回来查参数用的，可以按需跳读。",
            "To get a first successful run, read the [Quick Start](/quickstart/string-flow.html): it tunes nothing and just walks performer → MIDI → generate. This page is what you come back to when you want to change something, so jump around freely.",
          ),
        },
        {
          kind: "callout",
          tone: "tip",
          title: l("标签就是界面上的字", "The labels are the UI's own words"),
          text: l(
            "正文里用 代码样式 标出的都是界面上真实存在的标签，字符完全一致。找不到某个按钮时，在浏览器里按 Ctrl+F 搜标签名，比在截图里找快得多。",
            "Everything shown in code style is a label that really exists in the app, character for character. When you cannot find a button, Ctrl+F on the label in your browser beats hunting through the screenshot.",
          ),
        },
      ],
    },

    // ═════════════════════════════════════════════════════════════
    // 页签 1：角色设置
    // ═════════════════════════════════════════════════════════════
    {
      id: "tab-avatar",
      title: l("页签一：⚙ 角色设置", "Tab 1: ⚙ 角色设置 / ⚙ Avatar Setup"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "这一屏决定「谁在拉、拉的是哪种琴」，同时决定整段动画的采样密度（FPS）和运弓的基本手感。它分成上下两行四块卡片：上面一行是 01 角色选择 与 02 角色信息，下面一行是 03 生成参数 与 04 运弓策略。",
            "This screen decides who plays and on which instrument, and it also sets the animation's sampling density (FPS) and the basic feel of the bowing. It is four cards in two rows: 01 角色选择 / Select Avatar and 02 角色信息 / Avatar Info on top, 03 生成参数 / Generation Parameters and 04 运弓策略 / Bow Strategy below.",
          ),
        },
        {
          kind: "p",
          text: l(
            "跑通流程只需要在第一块里选一个角色，其余三块保持默认。要改效果时再回来动这里的参数。",
            "A first run only needs a performer chosen in the first card; the other three stay at their defaults. Come back here when you want to change the result.",
          ),
        },
      ],
      subs: [
        {
          id: "avatar-select",
          title: l(
            "01 角色选择：选谁在拉",
            "01 角色选择 / Select Avatar: choose the performer",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "最上面的一行下拉菜单列着已经保存的角色，选中谁，下面立刻显示谁的资料。卡片底部是整个软件里唯一能改「当前生效定弦」的地方。",
                "The dropdown at the top lists the saved performers; whoever you pick, their details appear right below. The bottom of this card is the only place in the app where you can change the tuning actually in effect.",
              ),
            },
            {
              kind: "table",
              head: [
                l("位置", "Item"),
                l("作用", "What it does"),
                l("取值", "Values"),
                l("默认", "Default"),
                l("怎么调", "When to change it"),
              ],
              rows: [
                [
                  l(
                    "角色下拉菜单（显示当前角色名）",
                    "Performer dropdown (shows the current name)",
                  ),
                  l(
                    "切换这次生成动画使用的演奏者。选中后下面的乐器和弦音高会立刻跟着换。",
                    "Switches which performer this run uses; the instrument and string pitches below follow immediately.",
                  ),
                  l(
                    "已保存的角色列表；列表为空时显示 未选择。",
                    "The saved performer list; shows 未选择 / Not selected when it is empty.",
                  ),
                  l("列表里的第一个", "The first entry in the list"),
                  l(
                    "换成另一种乐器（例如从提琴换到大提琴）就换角色，而不是改参数。",
                    "To change instrument (violin to cello, say), switch performer rather than changing a parameter.",
                  ),
                ],
                [
                  l("调弦方案:", "调弦方案: / Tuning scheme"),
                  l(
                    "临时覆盖当前生效的四根弦音高，让同一个角色用别的定弦演奏。它**只改弦音高**，不动乐器名称与品格范围。",
                    "Temporarily overrides the four open-string pitches so the same performer can play in a different tuning. It **only changes string pitches** — not the instrument name or the fret ranges.",
                  ),
                  l(
                    "小提琴 / 中提琴 / 大提琴 / 倍低音提琴；自定义定弦的角色显示 自定义。",
                    "小提琴 / Violin / 中提琴 / Viola / 大提琴 / Cello / 倍低音提琴 / Double Bass; a custom-tuned performer shows 自定义 / Custom.",
                  ),
                  l(
                    "跟随角色的 .violinist",
                    "Whatever the performer's .violinist says",
                  ),
                  l(
                    "同一段音乐在高把位来回挣扎时，换成更低的定弦往往比调参数有效。",
                    "When a passage keeps fighting in high positions, a lower tuning often helps more than any parameter.",
                  ),
                ],
                [
                  l(
                    "弦音高: 徽章（只读）",
                    "弦音高: / String pitch badges (read-only)",
                  ),
                  l(
                    "四根弦当前的空弦音高，一枚一枚列出来（截图里是 弦1: E5 这样的形式）。",
                    "The four open-string pitches, one badge each (shown as 弦1: E5 / String 1: E5 in the screenshot).",
                  ),
                  l("只读", "Read-only"),
                  l("来自角色配置", "From the performer profile"),
                  l(
                    "换过 调弦方案: 之后回来看一眼，确认真的换过去了。",
                    "After switching 调弦方案: / Tuning scheme, glance back here to confirm it took effect.",
                  ),
                ],
                [
                  l(
                    "N 根弦 / 乐器名徽章（只读）",
                    "N 根弦 / N strings and instrument badges (read-only)",
                  ),
                  l(
                    "当前角色的乐器类型与弦数，用来确认选对了人。",
                    "The current performer's instrument type and string count — a sanity check that you picked the right one.",
                  ),
                  l("只读", "Read-only"),
                  l("来自角色配置", "From the performer profile"),
                  l(
                    "看到 大提琴 却想做小提琴动画，就是选错了角色。",
                    "If it says 大提琴 / Cello and you wanted a violin, you picked the wrong performer.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "info",
              title: l(
                "什么时候会出现橙色提示",
                "When the orange hint appears",
              ),
              text: l(
                "当选中的 调弦方案: 与角色本身的定弦不一样时，卡片里会多出一行橙色小字 当前使用 {方案} 定弦（临时），并在下面重新列出当前生效的四根弦音高。它提醒你：这一版定弦只存在于本次会话的生成里，角色文件本身没被改写。",
                "When the chosen 调弦方案: / Tuning scheme differs from the performer's own tuning, an orange line appears — 当前使用 {方案} 定弦（临时） / Currently using {} tuning (temporary) — with the four pitches now in effect listed below it. It is a reminder that this tuning only exists for the generation in this session; the performer file itself is untouched.",
              ),
            },
          ],
        },
        {
          id: "avatar-info",
          title: l(
            "02 角色信息：看图、加人、删人",
            "02 角色信息 / Avatar Info: preview and manage performers",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "右上角这块默认显示当前角色的预览图，以及三个管理按钮。角色图片没配好时占位图会写 🎵 无图片。",
                "By default this card shows the current performer's preview image and three management buttons. If no image is configured, the placeholder reads 🎵 无图片 / 🎵 No image.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "✏ 修改 —— 打开编辑表单，字段预填当前角色的内容。",
                  "✏ 修改 / ✏ Edit — opens the edit form pre-filled with the current performer.",
                ),
                l(
                  "➕ 新增 —— 打开空白表单，建一个新角色。",
                  "➕ 新增 / ➕ Add — opens an empty form to create a new performer.",
                ),
                l(
                  "🗑 删除 —— 弹出确认窗（确认删除 标题 +「此操作不可撤销。」），点 ✅ 确认删除 才真的删；点 ❌ 取消 关掉。角色只剩一个时删不掉，会提示 不能删除最后一个角色。",
                  "🗑 删除 / 🗑 Delete — opens a confirmation window (确认删除 / Confirm Delete plus 此操作不可撤销。 / This action cannot be undone.). Only ✅ 确认删除 / ✅ Confirm Delete actually deletes; ❌ 取消 / ❌ Cancel closes it. With a single performer left, deletion is refused with 不能删除最后一个角色 / Cannot delete the last avatar.",
                ),
              ],
            },
            {
              kind: "p",
              text: l(
                "点 ✏ 修改 或 ➕ 新增 之后，这块卡片变成表单，三个字段加两个按钮：",
                "After ✏ 修改 / ✏ Edit or ➕ 新增 / ➕ Add, the card becomes a form with three fields and two buttons:",
              ),
            },
            {
              kind: "table",
              head: [
                l("字段", "Field"),
                l("填什么", "What goes in it"),
                l("必须吗", "Required"),
              ],
              rows: [
                [
                  l("名字:", "名字: / Name"),
                  l(
                    "角色的显示名，会出现在下拉菜单和生成结果的文件名里。不能与已有角色重名。",
                    "The performer's display name, used in the dropdown and in generated file names. Must not clash with an existing performer.",
                  ),
                  l(
                    "必填；留空报 角色名字不能为空，重名报 角色名字已存在。",
                    "Yes; blank gives 角色名字不能为空 / Avatar name cannot be empty, a duplicate gives 角色名字已存在 / Avatar name already exists.",
                  ),
                ],
                [
                  l("图片:", "图片: / Image"),
                  l(
                    "角色的预览图。点右边 📁 浏览 从磁盘选（只列 图片文件，即 png / jpg / jpeg / gif），选中后图片会被复制进 asset/img/。",
                    "The performer's preview image. 📁 浏览 / 📁 Browse picks one from disk (filtered to 图片文件 / Image files: png / jpg / jpeg / gif); the file is copied into asset/img/.",
                  ),
                  l(
                    "可留空，界面会显示 🎵 无图片。",
                    "Optional; the UI shows 🎵 无图片 / 🎵 No image when empty.",
                  ),
                ],
                [
                  l("配置:", "配置: / Config"),
                  l(
                    "角色的 .violinist 角色配置文件，📁 浏览 的过滤器就是 角色文件。乐器类型、品格范围、Blender 侧的骨骼与控制器定义全部来自这个文件。",
                    "The .violinist performer profile; the 📁 浏览 / 📁 Browse filter is literally 角色文件 / Avatar files. The instrument type, fret ranges and the rig/controller definitions used on the Blender side all come from this file.",
                  ),
                  l(
                    "必填；留空报 必须选择角色配置文件(.violinist)。",
                    "Yes; leaving it blank gives 必须选择角色配置文件(.violinist) / An avatar config file (.violinist) must be selected.",
                  ),
                ],
                [
                  l(
                    "💾 保存 / ❌ 取消",
                    "💾 保存 / 💾 Save / ❌ 取消 / ❌ Cancel",
                  ),
                  l(
                    "保存会写入角色列表并立刻重新加载，表单关闭；取消直接关掉表单，什么都不写。",
                    "Save writes the performer list, reloads it and closes the form; cancel closes the form and writes nothing.",
                  ),
                  l("——", "—"),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "截图里拍到的是 02 角色信息 的浏览状态；点 ✏ 修改 / ➕ 新增 之后展开的表单、以及删除确认窗没有单独截图，字段与按钮在上面的表里已经写全。",
                "The screenshot captures the browsing state of 02 角色信息 / Avatar Info; the form that opens from ✏ 修改 / ✏ Edit / ➕ 新增 / ➕ Add and the delete confirmation window are not pictured, so their fields and buttons are spelled out in the table above.",
              ),
            },
            {
              kind: "callout",
              tone: "warn",
              title: l(
                "乐器类型不在这里选",
                "The instrument is not chosen here",
              ),
              text: l(
                "界面上没有「这个角色是小提琴还是大提琴」的开关：乐器类型、品格范围都是从 .violinist 文件里读出来的。想换乐器，就换一个对应乐器的角色。角色列表为空时，界面会显示 暂无角色 和一枚 ➕ 新增角色 按钮。",
                "There is no switch for “is this performer a violin or a cello”: the instrument type and fret ranges are read from the .violinist file. To change instrument, use a performer of that instrument. When the list is empty the card shows 暂无角色 / No avatars yet and a ➕ 新增角色 / ➕ New Avatar button.",
              ),
            },
          ],
        },
        {
          id: "avatar-params",
          title: l(
            "03 生成参数：五个影响成品的旋钮",
            "03 生成参数 / Generation Parameters: five knobs that shape the result",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "这块卡片里的参数都不改变角色本身，只改变导出时的生成细节——卡片底部那行小字 这些参数只改变生成细节，不影响原有页面的业务流程。 说的就是这件事。改完任何一项都要重新生成（见 ▶ 执行操作 页）。",
                "Nothing in this card changes the performer itself — only how the export is generated, exactly as the small line at the bottom says: 这些参数只改变生成细节，不影响原有页面的业务流程。 / These parameters only change generation details and do not affect the existing workflow. After changing any of them you must generate again (see the ▶ 执行操作 / ▶ Execute tab).",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "What it does"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("怎么调", "How to tune it"),
              ],
              rows: [
                [
                  l("FPS", "FPS"),
                  l(
                    "动画的采样率：每秒写多少帧。生成的动画帧数就是「时长 × FPS」，指法检查页里显示的 动画帧 也是按它换算的。",
                    "The animation's sampling rate: how many frames are written per second. Frame count equals duration × FPS, and the 动画帧 / Frame read-out on the fingering tab is derived from it.",
                  ),
                  l(
                    "数值输入框，界面没有设上下限；步进 1。",
                    "A number field with no built-in limits; step 1.",
                  ),
                  l("60", "60"),
                  l(
                    "和插件所在工程的帧率保持一致最省事（常见 30 或 60）。调大更细腻，代价是文件更大、生成更慢。",
                    "Match the frame rate of the project you will use it in (30 or 60 are common). Higher is smoother but makes bigger files and slower runs.",
                  ),
                ],
                [
                  l(
                    "更高八度（配 启用 复选框）",
                    "更高八度 / Higher octave (with the 启用 / Enable checkbox)",
                  ),
                  l(
                    "勾上以后，解析 MIDI 时把每个音符整体下移一个八度（源码里就是每个音高 - 12）再映射到琴弦上。▶ 执行操作 页的 八度 会跟着显示 是。",
                    "When ticked, every note is shifted down one octave while parsing (literally - 12 per pitch) before being mapped onto the strings. The 八度 / Octave row on the ▶ 执行操作 / ▶ Execute tab then reads 是 / Yes.",
                  ),
                  l("勾选 / 不勾选", "On / off"),
                  l("不勾选（原始八度）", "Off (原始八度 / Original octave)"),
                  l(
                    "旋律整体偏高、映射到高音弦后把位吃紧或频繁换弦时勾上看看。注意界面文字与它实际做的方向相反，以听感为准。",
                    "Tick it when a high-register melody ends up in cramped high positions or keeps jumping strings. Note the label points the opposite way from what the code does — trust your ears.",
                  ),
                ],
                [
                  l("弓速", "弓速 / Bow speed"),
                  l(
                    "右手运弓的基准速度，单位是每秒走多少个弓位。它乘进每一弓的行程里，所以数值越大，一弓在弓段上走得越远，长音听起来更舒展。",
                    "The baseline speed of the right-hand bow, in bow positions per second. It scales the travel of every stroke, so larger values cover more of the bow and make long notes sound more expansive.",
                  ),
                  l(
                    "0.1 ~ 2.0 位置/秒，步进 0.1",
                    "0.1 – 2.0 位置/秒 / positions/sec, step 0.1",
                  ),
                  l("1.2", "1.2"),
                  l(
                    "慢曲子想要更宽的长弓 → 调大；音符很密、弓来不及换向 → 调小。",
                    "Slow music that wants broader strokes → raise it; dense passages where the bow cannot change direction fast enough → lower it.",
                  ),
                ],
                [
                  l("揉弦速度", "揉弦速度 / Vibrato speed"),
                  l(
                    "揉弦快慢的倍率。源码里揉弦周期 = 0.3 ÷ 倍率（秒），所以数值越大揉得越快。",
                    "A multiplier on vibrato rate. In the source the period is 0.3 ÷ multiplier seconds, so larger values vibrate faster.",
                  ),
                  l(
                    "0.5 ~ 2.0 倍率，步进 0.1",
                    "0.5 – 2.0 倍率 / Multiplier, step 0.1",
                  ),
                  l("1.0", "1.0"),
                  l(
                    "抒情长音听着太死板 → 调大；想要沉稳、缓慢的宽揉 → 调小。",
                    "Lyrical long notes sounding stiff → raise it; a slower, heavier vibrato → lower it.",
                  ),
                ],
                [
                  l("揉弦幅度", "揉弦幅度 / Vibrato amplitude"),
                  l(
                    "揉弦摆动幅度的倍率。源码里幅度除数 = 48 ÷ 倍率，所以数值越大，手指摆得越开。",
                    "A multiplier on vibrato depth. In the source the depth divisor is 48 ÷ multiplier, so larger values swing the finger wider.",
                  ),
                  l(
                    "0.5 ~ 2.0 倍率，步进 0.1",
                    "0.5 – 2.0 倍率 / Multiplier, step 0.1",
                  ),
                  l("1.0", "1.0"),
                  l(
                    "先调 揉弦速度 定快慢，再动幅度。揉弦幅度过大在小提琴上容易显得夸张。",
                    "Set the rate with 揉弦速度 / Vibrato speed first, then adjust depth. Too much depth reads as overwrought on a violin.",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "生效阶段不一样：FPS 与 更高八度 同时作用于 1. 生成指法 和 2. 生成动画；弓速 在指法阶段就写进了弓法记录；揉弦速度 与 揉弦幅度 只在 2. 生成动画 时起作用。想让改动生效，至少要重跑动画那一步；改了 FPS 或 更高八度 则建议从 1. 生成指法 重跑。",
                "They take effect at different stages: FPS and 更高八度 / Higher octave affect both 1. 生成指法 / 1. Generate Fingering and 2. 生成动画 / 2. Generate Animation; 弓速 / Bow speed is baked into the bowing record during the fingering stage; 揉弦速度 / Vibrato speed and 揉弦幅度 / Vibrato amplitude only matter in 2. 生成动画. To apply a change, re-run at least the animation step — and re-run from 1. 生成指法 after changing FPS or 更高八度.",
              ),
            },
          ],
        },
        {
          id: "avatar-bow",
          title: l(
            "04 运弓策略：换不换弓向",
            "04 运弓策略 / Bow Strategy: when the bow changes direction",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "这块卡片只有两个选项，决定右手什么时候换弓向。选中的那个会写在下面的方框里，并附一段说明。它对所有轨道生效，不能按轨道分别设置。",
                "This card has just two options, deciding when the right hand changes bow direction. The selected one is echoed in the box below with a short explanation. It applies to every track — there is no per-track setting.",
              ),
            },
            {
              kind: "table",
              head: [
                l("选项", "Option"),
                l("它的规则", "Its rule"),
                l("适合什么段落", "Good for"),
              ],
              rows: [
                [
                  l("长弓模式（默认）", "长弓模式 / Long bow (default)"),
                  l(
                    "尽量保持同一运弓动作的连续性，只有可能越界时才换向。",
                    "Keeps one bow stroke going as long as possible and only changes direction when the bow would run out of range.",
                  ),
                  l(
                    "旋律性段落、连奏，音色更连贯、弓向切换更少。",
                    "Melodic and legato passages — smoother tone, fewer direction changes.",
                  ),
                ],
                [
                  l("强制换弓模式", "强制换弓模式 / Force bow change"),
                  l(
                    "每个音符都开启新的运弓动作，方向交替。",
                    "Starts a fresh bow stroke on every note, alternating direction.",
                  ),
                  l(
                    "需要明确弓向切换的段落、快速音群、练习用的分解动作。",
                    "Passages that need explicit bow changes, fast note runs, and study-style articulation.",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "这个选择会影响 output/bow_recorder/ 里写出的弓法记录，所以改完要重跑 1. 生成指法（或直接 一键执行），只重跑动画那一步不会重新决定弓向。",
                "The choice is baked into the bowing record under output/bow_recorder/, so after changing it re-run 1. 生成指法 / 1. Generate Fingering (or just 一键执行 / Run All). Re-running only the animation step will not re-decide bow directions.",
              ),
            },
          ],
        },
      ],
    },

    // ═════════════════════════════════════════════════════════════
    // 页签 2：MIDI 处理
    // ═════════════════════════════════════════════════════════════
    {
      id: "tab-midi",
      title: l("页签二：🎵 MIDI处理", "Tab 2: 🎵 MIDI处理 / 🎵 MIDI"),
      image: { image: 1 },
      blocks: [
        {
          kind: "p",
          text: l(
            "这一屏负责把 MIDI 读进来、挑出要生成动画的轨道，并做可选的预处理。左栏是文件与处理参数，右栏是扫描出来的轨道列表和四个处理按钮。",
            "This screen loads the MIDI, picks the tracks to animate, and offers optional preprocessing. Files and processing parameters are on the left; the scanned track list and the four processing buttons are on the right.",
          ),
        },
      ],
      subs: [
        {
          id: "midi-file",
          title: l(
            "01 MIDI 文件：选文件与扫描",
            "01 MIDI 文件 / MIDI File: load and scan",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "选好文件后，卡片中间会显示文件名和完整路径；还没选的时候显示 未选择 MIDI 文件 与 支持 .mid 和 .midi 文件。。",
                "Once a file is loaded the card shows its name and full path; before that it reads 未选择 MIDI 文件 / No MIDI file selected and 支持 .mid 和 .midi 文件。 / Supports .mid and .midi files.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "选择 MIDI 文件 —— 打开文件对话框，只列 MIDI 文件（.mid / .midi）。选了别的扩展名不会替换当前文件，并在左栏提示 错误：文件扩展名不正确，期望 .mid 或 .midi，实际为 .{扩展名}；文件没有扩展名则提示 错误：选择的文件没有扩展名。",
                  "选择 MIDI 文件 / Select MIDI File — opens the file dialog filtered to MIDI 文件 / MIDI Files (.mid / .midi). Picking another extension does not replace the current file; the left column reports 错误：文件扩展名不正确，期望 .mid 或 .midi，实际为 .{扩展名} / Error: incorrect file extension; expected .mid or .midi, got .{扩展名}, and a file with no extension gives 错误：选择的文件没有扩展名 / Error: the selected file has no extension.",
                ),
                l(
                  "📥 将 MIDI 文件拖放到此处 —— 把文件从资源管理器直接拖进窗口也能加载，虚线框下面写着 支持 .mid 和 .midi 格式。",
                  "📥 将 MIDI 文件拖放到此处 / 📥 Drop a MIDI file here — dragging a file in from Explorer works too; the dashed box is captioned 支持 .mid 和 .midi 格式 / Supports .mid and .midi formats.",
                ),
                l(
                  "扫描 MIDI 信息 —— 解析文件，把每条有音符的轨道列到右边 03 轨道列表，同时把文本结果写到 output/current_midi_info.txt。还没选文件就点会提示 错误：请先选择MIDI文件；文件损坏则提示 MIDI文件不存在或格式错误。",
                  "扫描 MIDI 信息 / Scan MIDI Info — parses the file, lists every track that contains notes in 03 轨道列表 / Track List, and writes a text report to output/current_midi_info.txt. Clicking it with no file reports 错误：请先选择MIDI文件 / Error: please select a MIDI file first; a broken file reports MIDI文件不存在或格式错误 / MIDI file does not exist or has an invalid format.",
                ),
              ],
            },
            {
              kind: "note",
              text: l(
                "按钮下面的小字 扫描后可在右侧选择轨道；音符时值延长会在解析时自动完成，修剪为可选操作。 说明了两个要点：扫描之后才能勾轨道；音符时值延长是自动的，不需要你做任何操作。",
                "The caption under the buttons — 扫描后可在右侧选择轨道；音符时值延长会在解析时自动完成，修剪为可选操作。 / After scanning you can tick tracks on the right; note-duration extension happens automatically during parsing, and trimming is optional. — says two things: you can only tick tracks after scanning, and note-duration extension happens automatically, so there is nothing for you to do about it.",
              ),
            },
          ],
        },
        {
          id: "midi-params",
          title: l(
            "02 处理参数：通道与量化",
            "02 处理参数 / Processing Parameters: channel and quantization",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "这块卡片只有两个真正可调的参数，另外两行是只读的回显。",
                "Only two parameters here are actually adjustable; the other two rows are read-outs.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "What it does"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("怎么调", "How to tune it"),
              ],
              rows: [
                [
                  l("使用通道", "使用通道 / Use Channel"),
                  l(
                    "通道过滤。解析时只挑出指定通道的音符——-1 表示不过滤，文件里所有通道都参与。",
                    "Channel filtering: only notes on the given channel are parsed. -1 means no filter, so every channel in the file takes part.",
                  ),
                  l(
                    "-1 或 0 ~ 15 的通道号；数值输入，步进 1。",
                    "-1, or a channel number 0 – 15; a number field with step 1.",
                  ),
                  l("-1", "-1"),
                  l(
                    "文件里混进了伴奏或打击乐通道、却只想生成某一条通道的演奏时，填那个通道号——03 轨道列表 的 通道 一列显示的就是它。",
                    "When a file mixes in accompaniment or percussion channels and you only want one of them, enter that channel number — the 通道 / Channel column in 03 轨道列表 / Track List shows exactly these values.",
                  ),
                ],
                [
                  l("音符量化", "音符量化 / Note Quantization"),
                  l(
                    "把开始时间非常接近的音符合并成同一组（也就是同一帧的和弦/齐奏），合并窗口按每拍的 tick 数换算。",
                    "Merges notes that start close together into one group (a chord or unison on the same frame); the merge window is computed from the file's ticks per beat.",
                  ),
                  l(
                    "无量化 / 1/16 / 1/32 / 1/64 / 1/128；分母越大窗口越细。",
                    "无量化 / None / 1/16 / 1/32 / 1/64 / 1/128; a larger denominator means a finer window.",
                  ),
                  l("1/16", "1/16"),
                  l(
                    "弦乐齐奏或琶音被拆得太碎、弓法乱跳 → 保持或往粗调；想保留细腻的时间差 → 选 无量化。",
                    "If unisons or arpeggios get chopped into too many frames and the bowing gets jumpy, keep it or go coarser; to preserve fine timing, pick 无量化 / None.",
                  ),
                ],
              ],
            },
            {
              kind: "ul",
              items: [
                l(
                  "已选轨道数 —— 只读，当前已经加入处理的轨道条数。",
                  "已选轨道数 / Selected Track Count — read-only, how many tracks are currently part of the run.",
                ),
                l(
                  "已选择的轨道 —— 只读，一条轨道一枚 轨道 N 徽章；标了拨奏的显示成 轨道 N (拨奏)。一条都没加时显示 尚未加入轨道。",
                  "已选择的轨道 / Selected Tracks — read-only badges, one 轨道 N / Track N per track; pizzicato tracks read 轨道 N (拨奏). With nothing added it shows 尚未加入轨道 / No tracks added yet.",
                ),
              ],
            },
          ],
        },
        {
          id: "midi-tracks",
          title: l(
            "03 轨道列表：勾出事哪几条",
            "03 轨道列表 / Track List: tick the tracks you want",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "扫描结果就画在这张表里。上方两枚徽章分别是 已扫描 N 条轨道 与 已选 N 条；还没扫描时这里显示 等待扫描 MIDI 和 先在左侧选择文件并扫描，轨道信息会显示在这里。。注意只有含音符的轨道会列出来，空轨道是隐藏的。",
                "Scan results are drawn in this table. Two badges sit above it: 已扫描 N 条轨道 / N tracks scanned and 已选 N 条 / N selected. Before a scan it shows 等待扫描 MIDI / Waiting to scan MIDI and 先在左侧选择文件并扫描，轨道信息会显示在这里。 / Select a file on the left and scan first; track info will appear here. Only tracks containing notes are listed — empty ones are hidden.",
              ),
            },
            {
              kind: "table",
              head: [
                l("列", "Column"),
                l("读什么", "What it tells you"),
                l("能改吗", "Adjustable"),
              ],
              rows: [
                [
                  l("选择", "选择 / Select"),
                  l(
                    "这条轨道要不要参与生成。勾上是「要」。",
                    "Whether this track takes part in the run. Ticked means yes.",
                  ),
                  l("勾选框，可改", "Checkbox"),
                ],
                [
                  l("轨道", "轨道 / Track"),
                  l(
                    "MIDI 文件里的轨道号，从 0 开始。后面生成的文件名里带的就是这个号。",
                    "The track number inside the MIDI file, starting at 0. It is the number that appears in generated file names.",
                  ),
                  l("只读", "Read-only"),
                ],
                [
                  l("通道", "通道 / Channel"),
                  l(
                    "这条轨道使用的 MIDI 通道；文件里没有可用通道信息时显示 N/A。",
                    "The MIDI channel this track uses; shows N/A when the file has no usable channel information.",
                  ),
                  l("只读", "Read-only"),
                ],
                [
                  l("乐器", "乐器 / Instrument"),
                  l(
                    "文件里登记的乐器名。轨道名是自己命名的会显示成 乐器名 (轨道名)，自动生成的轨道名则只显示乐器名。",
                    "The instrument name recorded in the file. A custom track name shows as instrument (track name); auto-generated track names show the instrument alone.",
                  ),
                  l("只读", "Read-only"),
                ],
                [
                  l("音符数", "音符数 / Notes"),
                  l(
                    "这条轨道里有多少个音符——判断哪条是主旋律最快的办法。",
                    "How many notes the track holds — the quickest way to spot the melody.",
                  ),
                  l("只读", "Read-only"),
                ],
                [
                  l("拨奏", "拨奏 / Pizzicato"),
                  l(
                    "勾上表示这条轨道按拨奏处理（右手拨弦，不运弓），弓法记录会跳过它。加进列表后徽章上会带  (拨奏)。",
                    "Ticking this treats the track as pizzicato (plucked with the right hand, not bowed), and the bowing record skips it. Once added, the badge carries  (拨奏) / (pizzicato).",
                  ),
                  l("勾选框，可改", "Checkbox"),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "warn",
              title: l(
                "勾选不会自动生效",
                "Ticking alone does not commit anything",
              ),
              text: l(
                "在 选择 / 拨奏 两列里改的只是这张表的界面状态，必须再点右下角的 添加选中的轨道 才会写进本次生成。改了勾选就再点一次。",
                "Changes to the 选择 / Select / 拨奏 / Pizzicato columns only alter this table's UI state; you must click 添加选中的轨道 / Add Selected Tracks at the bottom right for them to count. Every time you change a tick, click it again.",
              ),
            },
          ],
        },
        {
          id: "midi-actions",
          title: l(
            "04 处理操作：一个必点、三个可选",
            "04 处理操作 / Actions: one required, three optional",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "四个按钮从上到下就是推荐顺序。第一个是每次生成都必点的；下面三个会把处理结果写成**新的 MIDI 文件**，都是可选的预处理。卡片底部的小字 建议流程：扫描轨道 -> 勾选需要的轨道 -> 添加轨道 -> 弦乐分轨 / 修剪 / 速度均一化。 说的就是这件事。",
                "The four buttons are in the recommended order. The first is required on every run; the three below it each write a **new MIDI file** and are optional preprocessing. The caption at the bottom of the card says exactly that: 建议流程：扫描轨道 -> 勾选需要的轨道 -> 添加轨道 -> 弦乐分轨 / 修剪 / 速度均一化。 / Suggested flow: scan tracks -> check the tracks you need -> add tracks -> split quartet / trim / normalize velocity.",
              ),
            },
            {
              kind: "table",
              head: [
                l("按钮", "Button"),
                l("点了会发生什么", "What clicking does"),
                l("什么时候用", "When to use it"),
              ],
              rows: [
                [
                  l("添加选中的轨道", "添加选中的轨道 / Add Selected Tracks"),
                  l(
                    "清空 已选择的轨道，把 选择 列勾上的轨道按轨道号排序写进去（拨奏标记一起写入），并提示 轨道添加成功。",
                    "Clears 已选择的轨道 / Selected Tracks and writes in the tracks ticked in the 选择 / Select column, sorted by track number, pizzicato flags included. Reports 轨道添加成功 / Tracks added successfully.",
                  ),
                  l(
                    "每次生成都必须点一次；改了勾选后要再点。",
                    "Every run needs it; click again after changing ticks.",
                  ),
                ],
                [
                  l(
                    "弦乐分轨（四重奏）",
                    "弦乐分轨（四重奏） / Split String Quartet",
                  ),
                  l(
                    "把选中轨道的音符按声部拆成 Vn1 / Vn2 / Va / Vc，在源文件旁边写出 {原名}_quartet.mid（0 号是速度/拍号轨，后面四条是乐器轨），并提示 弦乐分轨成功！输出文件：{...}。",
                    "Splits the selected tracks' notes into Vn1 / Vn2 / Va / Vc and writes {原名}_quartet.mid next to the source file — track 0 carries tempo and time signature, followed by four instrument tracks. Reports 弦乐分轨成功！输出文件：{...} / String quartet split successful! Output file: {...}.",
                  ),
                  l(
                    "想用四人编制的弦乐四重奏来演奏一段钢琴或合成器的 MIDI 时。",
                    "When you want a piano or synth MIDI played by a four-player string quartet.",
                  ),
                ],
                [
                  l("修剪 MIDI 音符", "修剪 MIDI 音符 / Trim MIDI Notes"),
                  l(
                    "把重叠、多余的音符时值修整干净，在源文件旁边写出 {原名}_trimmed.mid，并提示 修剪成功！输出文件：{...}。",
                    "Cleans up overlapping or redundant note durations and writes {原名}_trimmed.mid next to the source file. Reports 修剪成功！输出文件：{...} / Trim successful! Output file: {...}.",
                  ),
                  l(
                    "原曲音符互相压着、生成出来手指抢位时。",
                    "When the source notes overlap each other and the generated fingering fights for the same position.",
                  ),
                ],
                [
                  l("速度均一化处理", "速度均一化处理 / Normalize Velocity"),
                  l(
                    "把所有音符的力度统一，在源文件旁边写出 {原名}_velocity_normalized.mid，并提示 速度均一化处理成功！输出文件：{...}。力度直接参与弓速计算，统一之后弓法不会忽长忽短。",
                    "Normalizes the velocity of every note and writes {原名}_velocity_normalized.mid next to the source file. Reports 速度均一化处理成功！输出文件：{...} / Velocity normalization successful! Output file: {...}. Velocity feeds into bow speed, so normalizing keeps strokes from lurching between short and long.",
                  ),
                  l(
                    "同一段旋律里强弱悬殊、长短弓差别大到不自然时。",
                    "When dynamics within one phrase swing so widely that stroke lengths look unnatural.",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "三个处理按钮都不会覆盖你的原文件，它们各自生成一份新 MIDI。处理完要回到 01 MIDI 文件 选那份新文件、重新 扫描 MIDI 信息、重新 添加选中的轨道，然后才能用它生成动画。没选文件或没勾轨道就点，会分别提示 错误：请先选择MIDI文件 与 错误：请先选择至少一个轨道。",
                "None of the three processing buttons overwrites your original; each writes a new MIDI. Afterwards go back to 01 MIDI 文件 / MIDI File, load that new file, 扫描 MIDI 信息 / Scan MIDI Info again and 添加选中的轨道 / Add Selected Tracks again before generating from it. Clicking them with no file or no ticked tracks reports 错误：请先选择MIDI文件 / Error: please select a MIDI file first and 错误：请先选择至少一个轨道 / Error: please select at least one track respectively.",
              ),
            },
          ],
        },
      ],
    },

    // ═════════════════════════════════════════════════════════════
    // 页签 3：执行操作
    // ═════════════════════════════════════════════════════════════
    {
      id: "tab-execute",
      title: l("页签三：▶ 执行操作", "Tab 3: ▶ 执行操作 / ▶ Execute"),
      image: { image: 2 },
      blocks: [
        {
          kind: "p",
          text: l(
            "这一屏只管两件事：开始生成，和看日志。左边三块卡片都是回显，没有可调参数；右边整块是控制台。",
            "This screen does two things: start a run and show the log. The three cards on the left are read-outs with no parameters; the whole right side is the console.",
          ),
        },
      ],
      subs: [
        {
          id: "exec-avatar",
          title: l(
            "01 角色概览：确认是谁在拉",
            "01 角色概览 / Avatar Overview: confirm who is playing",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "这块卡片把 ⚙ 角色设置 页的选择原样搬过来，只读。要改就回那一页。",
                "This card mirrors the ⚙ 角色设置 / ⚙ Avatar Setup tab, read-only. Go back there to change anything.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "角色名 + 乐器徽章 + 八度徽章 —— 八度徽章显示 原始八度 或 高八度，对应 更高八度 是否勾选。",
                  "Performer name plus an instrument badge and an octave badge — the octave badge reads 原始八度 / Original octave or 高八度 / Higher octave, matching the 更高八度 / Higher octave checkbox.",
                ),
                l(
                  "FPS / 八度 / 弓速 三行 —— 分别是采样率、八度开关（是 / 否）和弓速（形如 1.2 位置/秒）。",
                  "Three rows: FPS, 八度 / Octave (是 / Yes / 否 / No) and 弓速 / Bow speed (shown as 1.2 位置/秒 / 1.2 positions/sec).",
                ),
                l(
                  "一个角色都没选时，这里显示 未选择角色 与 请先在角色设置页选择角色文件。；角色索引异常时显示 角色索引无效。",
                  "With no performer selected it shows 未选择角色 / No avatar selected and 请先在角色设置页选择角色文件。 / Please select an avatar file on the avatar setup page first.; an invalid index shows 角色索引无效 / Invalid avatar index.",
                ),
              ],
            },
          ],
        },
        {
          id: "exec-midi",
          title: l(
            "02 MIDI 参数：确认拉的是哪首",
            "02 MIDI 参数 / MIDI Parameters: confirm what is being played",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "同样只读：文件名与完整路径在上，下面是三个关键值。",
                "Also read-only: the file name and full path on top, three key values below.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "轨道 —— 已加入处理的轨道条数，形如 1 条。",
                  "轨道 / Track — how many tracks are in the run, shown as 1 条 / 1 track.",
                ),
                l(
                  "通道 —— 当前 使用通道 的值，-1 表示不过滤。",
                  "通道 / Channel — the current 使用通道 / Use Channel value; -1 means no filter.",
                ),
                l(
                  "已选轨道 —— 轨道号列表（拨奏轨写成 N(拨奏)），一条都没有时显示 无。",
                  "已选轨道 — the list of track numbers (pizzicato tracks as N(拨奏) / N (pizzicato)), or 无 / None when there are none.",
                ),
              ],
            },
            {
              kind: "p",
              text: l(
                "下面三枚状态徽章是执行进度，按顺序变色：初始化待执行 → 初始化已完成、指法待生成 → 指法已生成、动画待生成 → 动画已生成。生成卡住时看它们就知道停在哪一步。没选 MIDI 时这里显示 未选择 MIDI 文件 与 请先在 MIDI 处理页完成文件选择与轨道筛选。。",
                "Three status badges underneath track progress and turn over in order: 初始化待执行 / Initialization pending → 初始化已完成 / Initialization done, 指法待生成 / Fingering pending → 指法已生成 / Fingering generated, 动画待生成 / Animation pending → 动画已生成 / Animation generated. They tell you exactly which step a stuck run died on. With no MIDI loaded the card shows 未选择 MIDI 文件 / No MIDI file selected and 请先在 MIDI 处理页完成文件选择与轨道筛选。 / Please finish file selection and track filtering on the MIDI page first..",
              ),
            },
          ],
        },
        {
          id: "exec-operations",
          title: l(
            "03 执行操作：分步还是全自动",
            "03 执行操作 / Operations: step by step or all at once",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "这块卡片上下一共三行按钮，分两步执行和快速执行两种跑法。长曲子建议先用 1. 生成指法 确认轨道没选错，再 2. 生成动画。",
                "Three buttons in two groups: a step-by-step route and a one-click route. On a long piece, run 1. 生成指法 / 1. Generate Fingering first to confirm the tracks are right, then 2. 生成动画 / 2. Generate Animation.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "分步执行 → 1. 生成指法 —— 初始化系统、解析 MIDI、算左手指法与右手指法，写出 output/left_hand_recorder/{MIDI}_{轨道}.json 与 output/bow_recorder/{MIDI}_{轨道}.json。弓速、运弓策略、FPS、更高八度、使用通道、音符量化 都在这一步生效。",
                  "分步执行 / Step by step → 1. 生成指法 / 1. Generate Fingering — initializes, parses the MIDI, computes left-hand fingering and right-hand bowing, and writes output/left_hand_recorder/{MIDI}_{轨道}.json plus output/bow_recorder/{MIDI}_{轨道}.json. This is where 弓速 / Bow speed, the bow strategy, FPS, 更高八度 / Higher octave, 使用通道 / Use Channel and 音符量化 / Note Quantization take effect.",
                ),
                l(
                  "分步执行 → 2. 生成动画 —— 读上面那两份记录器，写出左手动画、右手动画、琴弦动画和活动曲线，最后汇成 output/final_result/{角色}_{MIDI}.string_flow。它依赖第一步的结果，文件不在时会报 错误: 左手指法文件不存在: {路径} 或 错误: 右手指法文件不存在: {路径}。",
                  "分步执行 / Step by step → 2. 生成动画 / 2. Generate Animation — reads those two recorders, writes the left-hand animation, right-hand animation, string animation and activity curve, and finally assembles output/final_result/{角色}_{MIDI}.string_flow. It depends on step 1: a missing file reports 错误: 左手指法文件不存在 / Error: left hand fingering file does not exist: {路径} or 错误: 右手指法文件不存在 / Error: right hand fingering file does not exist: {路径}.",
                ),
                l(
                  "快速执行 → 一键执行 —— 把上面两步连着跑完，控制台按 1/4、2/4、3/4、4/4 汇报进度，最后一行是 一键执行完成。一般第一次就用它。",
                  "快速执行 / Quick run → 一键执行 / Run All — runs both steps back to back, reporting 1/4, 2/4, 3/4, 4/4 as it goes and finishing with 一键执行完成 / All steps complete. This is usually what you want on the first try.",
                ),
                l(
                  "❌ 取消 —— 只在执行期间出现，紧挨着 正在执行...。点它停止当前任务并打印 执行已取消；已经写出的文件会留在 output/ 里，可以重新跑。",
                  "❌ 取消 / ❌ Cancel — only appears while a run is going, next to a 正在执行... / Running... label. It stops the current task and prints 执行已取消 / Execution cancelled; whatever was already written stays in output/ and you can run again.",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "tip",
              title: l("跑之前先扫一眼左栏", "Glance at the left column first"),
              text: l(
                "生成耗时和音符数成正比，选错轨道再重跑很浪费。点生成前只需确认两件事：01 角色概览 里的人对，02 MIDI 参数 里的轨道对。",
                "Run time scales with note count, so a wrong track wastes real time. Before clicking generate, check just two things: the performer in 01 角色概览 / Avatar Overview and the tracks in 02 MIDI 参数 / MIDI Parameters.",
              ),
            },
          ],
        },
        {
          id: "exec-console",
          title: l(
            "控制台输出：进度、日志与复制",
            "控制台输出 / Console Output: progress, log and copy",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "右边整块都是控制台。右上角三个按钮管内容，下面两枚徽章管状态：日志 N 条 与 状态：运行中 / 状态：空闲。没有内容时显示 暂无输出 与 执行日志会在这里持续刷新。。",
                "The whole right side is the console. Three buttons at the top right manage its content, and two badges below report state: 日志 N 条 / N logs plus 状态：运行中 / Status: running / 状态：空闲 / Status: idle. When it is empty you see 暂无输出 / No output yet and 执行日志会在这里持续刷新。 / Execution logs will keep refreshing here.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "清空信息 —— 清掉当前控制台里的全部日志，不影响已生成的文件。",
                  "清空信息 / Clear — clears the log; generated files are untouched.",
                ),
                l(
                  "复制全部 —— 把全部日志复制到剪贴板，提示 控制台输出已复制到剪贴板；没有内容时提示 控制台无内容可复制。贴给别人排查问题时用这个。",
                  "复制全部 / Copy All — copies the whole log, reporting 控制台输出已复制到剪贴板 / Console output copied to clipboard (or 控制台无内容可复制 / Console has no content to copy when empty). This is what you paste when asking for help.",
                ),
                l(
                  "复制结果 —— 只复制最近一次生成出来的结果文件路径，也就是 .string_flow 的绝对路径，提示 结果路径已复制到剪贴板：{路径}；还没生成过会提示 尚未找到可复制的结果路径。",
                  "复制结果 / Copy Result — copies just the path of the most recent result file, i.e. the absolute path of the .string_flow, reporting 结果路径已复制到剪贴板 / Result path copied to clipboard: {路径}; before any run it says 尚未找到可复制的结果路径 / No result path available to copy.",
                ),
              ],
            },
            {
              kind: "p",
              text: l(
                "日志按级别着色，不用逐字读：普通信息是等宽的浅色字，成功是绿字（左手指法已保存到: ...、弓法记录已成功导出到: ...、左手动画已保存到: ...、右手动画已保存到: ...、弦动画已保存到: ...），警告是黄字，错误是红字。长任务期间还会持续打印 进度: 501/1670, 速度: 368.37 步/秒。",
                "The log is colour-coded so you do not have to read every line: plain info is light monospace, success is green (左手指法已保存到 / Left hand fingering saved to: ..., 弓法记录已成功导出到 / Bow recorder exported to: ..., 左手动画已保存到 / Left hand animation saved to: ..., 右手动画已保存到 / Right hand animation saved to: ..., 弦动画已保存到 / String animation saved to: ...), warnings are yellow and errors red. Long runs keep printing 进度 / Progress: 501/1670, 速度 / speed: 368.37 steps/sec.",
              ),
            },
            {
              kind: "note",
              text: l(
                "想知道到底生成了哪些文件，看最后几行绿色的 已保存到 就够；想知道成品在哪，用 复制结果。",
                "To see which files were produced, read the green 已保存到 / Saved to lines at the end; to find the deliverable, use 复制结果 / Copy Result.",
              ),
            },
          ],
        },
      ],
    },

    // ═════════════════════════════════════════════════════════════
    // 页签 4：指法检查
    // ═════════════════════════════════════════════════════════════
    {
      id: "tab-fingering",
      title: l("页签四：🎼 指法检查", "Tab 4: 🎼 指法检查 / 🎼 Fingering"),
      image: { image: 3 },
      blocks: [
        {
          kind: "p",
          text: l(
            "这一屏是**核对工具**：把小提琴左手按弦画成一张「弦 × 时间」的 TAB 谱面，配上试听，让你在进插件之前就能听出来、看出来指法对不对。它读的是 output/left_hand_recorder/ 里已经生成好的文件，本身不写任何文件，也不改任何东西。",
            "This screen is a **checking tool**: it draws the left-hand fingering as a string-by-time TAB score with playback, so you can hear and see whether the fingering is right before opening the plugin. It reads what is already in output/left_hand_recorder/ and writes nothing.",
          ),
        },
        {
          kind: "p",
          text: l(
            "切到这个页签时窗口会自动最大化，因为谱面越宽一次能看的音越多；之后你可以自己还原窗口大小。没有可看的文件时，这一屏会直接告诉你缺哪一步。",
            "Switching to this tab maximizes the window automatically, since a wider score shows more notes at once; you can resize it back afterwards. When there is nothing to show, the screen tells you which step is missing.",
          ),
        },
      ],
      subs: [
        {
          id: "tab-source",
          title: l(
            "01 指法文件：看的是哪一份",
            "01 指法文件 / Fingering file: which record is loaded",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "左上角这块卡片负责选文件与汇总统计。下拉菜单里列的是**当前 MIDI 名下的全部 recorder 文件**：当前设置对应的那一份标着「当前设置」并排在最前，其它同名文件（换过轨道组合留下的）排在后面，切过去就能对比两版指法的差别。",
                "This card selects the file and summarizes it. The dropdown lists **every recorder file sharing the current MIDI name**: the one matching your current settings is marked and sorted first, and other files with the same prefix (left over from earlier track combinations) follow, so you can switch and compare two versions.",
              ),
            },
            {
              kind: "table",
              head: [l("位置", "Item"), l("显示什么", "What it shows")],
              rows: [
                [
                  l("指法文件下拉菜单", "Fingering file dropdown"),
                  l(
                    "可选文件名与大小。当前设置对应的那份写成 名字（N KB，当前设置），其它写成 名字（N KB）。换文件会立刻重新解析。",
                    "Selectable file names and sizes. The one matching your settings reads 名字（N KB，当前设置） / name (N KB, current settings); the rest read 名字（N KB） / name (N KB). Switching re-parses immediately.",
                  ),
                ],
                [
                  l("按弦音 N", "按弦音 N / N stopped notes"),
                  l(
                    "谱面里画出来的按弦音总数。",
                    "Total number of stopped notes drawn on the score.",
                  ),
                ],
                [
                  l("发声音头 N", "发声音头 N / N onsets"),
                  l(
                    "有多少个时刻真的在发声（每个发声时刻算一个音头）。它比 按弦音 少时，说明存在同帧双音/和弦。",
                    "How many moments actually sound (one onset per moment). When it is lower than 按弦音 / stopped notes, some frames hold double stops or chords.",
                  ),
                ],
                [
                  l("时长 N 秒", "时长 N 秒 / N seconds long"),
                  l("这份指法覆盖的总时长。", "Total span this record covers."),
                ],
                [
                  l("已忽略占位 N", "已忽略占位 N / N placeholders ignored"),
                  l(
                    "被忽略的记录条目数：按住但不发声（Keep）、手指就位悬停（Open）这类都不算发声，不进谱面。",
                    "Records that were skipped: held-but-silent (Keep) and hovering (Open) states do not sound, so they never reach the score.",
                  ),
                ],
                [
                  l("校验提示 N", "校验提示 N / N checks"),
                  l(
                    "自查发现的问题条数，有问题时这枚徽章是红色的。逐条解释见本节最后的《校验提示》。",
                    "How many issues the self-check found; the badge turns red when there are any. Each category is explained under “校验提示 / checks” below.",
                  ),
                ],
                [
                  l(
                    "弦位（0 = 最高音弦）：",
                    "弦位（0 = 最高音弦）： / Strings (0 = highest):",
                  ),
                  l(
                    "图例，一枚徽章一根弦，例如 0 弦 E5。和谱面左侧的弦名一一对应。",
                    "The legend, one badge per string such as 0 弦 E5 / String 0 E5, matching the string names on the left edge of the score.",
                  ),
                ],
                [
                  l(
                    "Ctrl + 滚轮缩放，左右拖动平移",
                    "Ctrl + 滚轮缩放，左右拖动平移 / Ctrl + wheel to zoom, drag sideways to pan",
                  ),
                  l(
                    "谱面操作提示。缩放以光标所在时间为锚点，平移只改视窗，都不会改动数据。",
                    "Score controls. Zoom is anchored at the time under the cursor and panning only moves the view; neither touches the data.",
                  ),
                ],
              ],
            },
            {
              kind: "p",
              text: l(
                "找不到文件时这一屏会分三种情况提示：还没选 MIDI → 还没有选择 MIDI（请先在「MIDI 处理」页选择 MIDI 文件与轨道，再回到「执行操作」页生成指法。）；MIDI 选了但目录里没有同名记录 → 找不到指法文件（并写出它期望的路径）；文件在但里面没有发声记录 → 指法文件是空的。",
                "When no file is available the screen distinguishes three cases: no MIDI chosen → 还没有选择 MIDI / No MIDI selected yet (请先在「MIDI 处理」页选择 MIDI 文件与轨道，再回到「执行操作」页生成指法。 / Pick a MIDI file and its tracks on the MIDI page, then generate fingering on the Execute page.); a MIDI but no matching record on disk → 找不到指法文件 / No fingering file found (it prints the path it expected); a file with no sounding notes → 指法文件是空的 / The fingering file is empty.",
              ),
            },
          ],
        },
        {
          id: "tab-note",
          title: l(
            "02 当前音符：点谁看谁",
            "02 当前音符 / Selected note: details of the note you click",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "在谱面上点一个品号徽标，右上角这块卡片就显示那个音的全部信息；没点之前显示 还没有选中音符 与 在谱面上点一个品号，这里会显示该音符的详细信息。。",
                "Click any fret badge on the score and this card shows everything about that note; before that it reads 还没有选中音符 / No note selected and 在谱面上点一个品号，这里会显示该音符的详细信息。 / Click a fret badge on the tab to see that note's details here.",
              ),
            },
            {
              kind: "table",
              head: [l("字段", "Field"), l("含义", "Meaning")],
              rows: [
                [
                  l("时间", "时间 / Time"),
                  l(
                    "这个音在曲子里的秒数（形如 7.704 秒）。",
                    "When the note sounds, in seconds (for example 7.704 秒 / 7.704 s).",
                  ),
                ],
                [
                  l("动画帧", "动画帧 / Frame"),
                  l(
                    "换算成动画帧的帧号（秒 × FPS）。要和插件时间线对齐时看这个。",
                    "The same moment as an animation frame (seconds × FPS) — the number to match against your plugin timeline.",
                  ),
                ],
                [
                  l("弦", "弦 / String"),
                  l(
                    "第几根弦，括号里是该弦的空弦音，形如 2 弦（空弦 D4）。",
                    "Which string, with its open pitch in brackets, e.g. 2 弦（空弦 D4） / String 2 (open D4).",
                  ),
                ],
                [
                  l("品位", "品位 / Fret"),
                  l(
                    "按在第几品；空弦音显示 0。",
                    "Which fret is stopped; open strings show 0.",
                  ),
                ],
                [
                  l("手指", "手指 / Finger"),
                  l(
                    "用第几根手指按，形如 第 3 指；空弦音显示 空弦（无需按指）。",
                    "Which finger presses it, e.g. 第 3 指 / finger 3; open strings show 空弦（无需按指） / open string (no finger).",
                  ),
                ],
                [
                  l("音高", "音高 / Pitch"),
                  l("这个音实际发出来的音名。", "The sounding pitch name."),
                ],
                [
                  l("发声时长", "发声时长 / Duration"),
                  l(
                    "这个音要按住多久（秒）。它对应谱面上徽标右侧那条延音线。",
                    "How long the note is held, in seconds — the sustain line to the right of the badge on the score.",
                  ),
                ],
              ],
            },
          ],
        },
        {
          id: "tab-score",
          title: l("谱面：怎么读这张 TAB", "The score: how to read the TAB"),
          blocks: [
            {
              kind: "p",
              text: l(
                "谱面占满整屏宽度，从上到下四条弦线就是 0 ~ 3 号弦，最上面是最高音弦（截图里 0 E5），最下面是 3 G3。每根弦线左端都写着弦号和空弦音，和 01 指法文件 里的图例一致。",
                "The score spans the full width. Four lines run top to bottom for strings 0 to 3, highest pitch on top (0 E5 in the screenshot) and 3 G3 at the bottom. Each line is labelled on the left with its number and open pitch, matching the legend in 01 指法文件 / Fingering file.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "**品号徽标**：方块里的数字就是品位。三种画法——空弦音是浅色底、实按是实心高亮、拨奏是描边款，一眼能区分。",
                  "**Fret badges**: the number in the box is the fret. Three styles tell them apart at a glance — light for open strings, solid for stopped notes, outlined for pizzicato.",
                ),
                l(
                  "**手指号**：徽标正上方的小数字是 1 ~ 4 指；空弦音没有手指号。",
                  "**Finger number**: the small digit just above a badge is finger 1 – 4; open strings have none.",
                ),
                l(
                  "**延音线**：徽标右侧的横线表示这个音要按住多久，长度来自记录里的发声时长。",
                  "**Sustain line**: the horizontal line to the right of a badge shows how long the note is held, taken from the recorded duration.",
                ),
                l(
                  "**校验标记**：谱面顶端的小竖线标出校验提示的位置——红色是需要处理的警告，灰色是提醒。点 01 指法文件 里的提示可以跳到对应帧。",
                  "**Check markers**: the small ticks along the top of the score mark where the self-check flagged something — red for warnings, grey for notices. Clicking an item in 01 指法文件 / Fingering file jumps to that frame.",
                ),
                l(
                  "**全曲密度条**：谱面最上方那条细长条把整曲的音符密度画成直方图，上面还叠着当前视窗和播放头。拖动它可以直接跳到某一段，密度高的地方音符多，适合快速定位难段。",
                  "**Density strip**: the thin bar above the score is a histogram of note density across the whole piece, with the current view window and playhead drawn on top. Drag it to jump to a section — dense stretches have more notes and are usually where the trouble is.",
                ),
                l(
                  "**时间刻线**：每根竖线上方标着秒数（如 10.9s），刻度间隔会自动疏密，放大时更细。",
                  "**Time ticks**: each vertical line is labelled in seconds (like 10.9s); the interval adapts, getting finer as you zoom in.",
                ),
              ],
            },
            {
              kind: "p",
              text: l(
                "谱面上方两行小字是当前视图状态：视窗 6.80s - 17.77s 是正在显示的时间范围，缩放 35.4× 是相对整曲的放大倍数。放大只为了让一个徽标有十几个像素好点，不影响数据。",
                "Two small lines above the score describe the view: 视窗 6.80s - 17.77s / View 6.80s - 17.77s is the time range on screen and 缩放 35.4× / Zoom 35.4x is the magnification relative to the whole piece. Zooming only widens badges from a few pixels so they can be clicked; the data is untouched.",
              ),
            },
          ],
        },
        {
          id: "tab-transport",
          title: l("播放控制条：对谱试听", "Transport: play it and listen"),
          blocks: [
            {
              kind: "p",
              text: l(
                "谱面下面一整行是播放控制。试听的音源就是你选的那份 MIDI 本身（含伴奏），所以听到的和谱面上看到的必然一致。",
                "The row under the score is the transport. Playback uses the MIDI file you loaded, accompaniment included, so what you hear always matches what the score shows.",
              ),
            },
            {
              kind: "table",
              head: [l("控件", "Control"), l("作用", "What it does")],
              rows: [
                [
                  l("▶ 播放 / ⏸ 暂停", "▶ 播放 / ▶ Play / ⏸ 暂停 / ⏸ Pause"),
                  l(
                    "按 FPS 推进播放头并同时发声。播放中视窗会强制跟着播放头走，保证它始终可见。空格或回车也能切换播放/暂停。",
                    "Advances the playhead at FPS and plays the notes. While playing, the view window follows the playhead so it stays visible. Space or Enter toggles playback too.",
                  ),
                ],
                [
                  l("⏹ 回到开头", "⏹ 回到开头 / ⏹ Back to start"),
                  l(
                    "播放头回到 0 秒，同时复位视窗。",
                    "Returns the playhead to 0 seconds and resets the view.",
                  ),
                ],
                [
                  l("时间显示", "Time read-out"),
                  l(
                    "形如 1.22 秒 · 第 73.5 帧：左边是当前秒数，右边是换算出来的帧号。",
                    "Reads like 1.22 秒 · 第 73.5 帧 / 1.22s · frame 73.5: current seconds on the left, the equivalent frame number on the right.",
                  ),
                ],
                [
                  l("复位视窗", "复位视窗 / Reset view"),
                  l(
                    "缩放和平移回到默认状态（默认显示约 6 秒那一屏）。",
                    "Restores zoom and pan to the default, which shows roughly a six-second window.",
                  ),
                ],
                [
                  l("试听音源：...", "试听音源：... / Audio source: ..."),
                  l(
                    "形如 试听音源：MIDI 原曲 393.9 秒 / 4400 个音（含伴奏）；没选 MIDI 时显示 试听音源：未选择 MIDI。",
                    "Reads like 试听音源：MIDI 原曲 393.9 秒 / 4400 个音（含伴奏）; with no MIDI it shows 试听音源：未选择 MIDI / Audio source: no MIDI selected.",
                  ),
                ],
                [
                  l("MIDI 输出 下拉菜单", "MIDI 输出 / MIDI output dropdown"),
                  l(
                    "选播放用哪个 MIDI 输出设备（截图里是 Microsoft GS Wavetable Synth）。没有可用设备时显示 无可用输出设备。",
                    "Picks which MIDI output device plays (the screenshot shows Microsoft GS Wavetable Synth). With none available it shows 无可用输出设备 / No output device.",
                  ),
                ],
                [
                  l("刷新设备", "刷新设备 / Refresh devices"),
                  l(
                    "重新枚举 MIDI 输出设备，装了声卡或软音源之后点一下。刷新会先暂停播放。",
                    "Re-enumerates the MIDI outputs — click it after installing a sound card or software synth. Refreshing pauses playback first.",
                  ),
                ],
                [
                  l("音量", "音量 / Volume"),
                  l(
                    "试听音量滑块，从左到右由小到大。",
                    "Playback volume slider, quiet on the left and loud on the right.",
                  ),
                ],
                [
                  l("试音", "试音 / Test tone"),
                  l(
                    "发一个测试音，用来确认设备选对了、音量听得到。",
                    "Fires a test tone so you can confirm the device and volume.",
                  ),
                ],
                [
                  l("状态文字", "Status text"),
                  l(
                    "MIDI 输出：{设备} 表示已打开；MIDI 输出：未启用（点播放时自动打开） 表示还没开始播；MIDI 输出不可用：{原因} 表示设备打不开，这时谱面照常走，只是不出声。",
                    "MIDI 输出：{设备} / MIDI output: {} means it is open; MIDI 输出：未启用（点播放时自动打开） / MIDI output: idle (opens automatically on play) means playback has not started; MIDI 输出不可用：{原因} / MIDI output unavailable: {} means the device refused to open — the score still scrolls, it is just silent.",
                  ),
                ],
                [
                  l(
                    "空格播放 / 暂停，← → 跳音",
                    "空格播放 / 暂停，← → 跳音 / Space to play / pause, ← → to jump between notes",
                  ),
                  l(
                    "快捷键提示：空格切播放/暂停，左右方向键在音符之间前后跳。",
                    "Keyboard hints: Space toggles playback, the arrow keys jump between notes.",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "这一屏的所有操作都是只读的：缩放、平移、跳帧、播放都不会改动任何文件，可以放心乱点。",
                "Everything on this screen is read-only: zooming, panning, jumping and playing never modify a file, so click freely.",
              ),
            },
          ],
        },
        {
          id: "tab-checks",
          title: l(
            "校验提示：谱面外的那份自查清单",
            "校验提示 / checks: the self-check list",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "除了画谱面，程序还会顺手检查生成出来的指法是否合理，问题汇总成 01 指法文件 里的 校验提示 N 徽章，并在谱面顶端打上标记。点一条提示就能跳到它指的那一帧，对着谱面判断要不要管。",
                "Besides drawing the score, the app checks whether the generated fingering makes sense. Findings are summarized in the 校验提示 N / N checks badge on 01 指法文件 / Fingering file and marked along the top of the score. Click a finding to jump to its frame and judge for yourself whether it matters.",
              ),
            },
            {
              kind: "table",
              head: [
                l("类别", "Category"),
                l("什么情况会报", "What triggers it"),
                l("说明", "Notes"),
              ],
              rows: [
                [
                  l("弦号", "弦号 / String index"),
                  l(
                    "弦号落到了 0 ~ 3 之外，提示形如 {N} 弦超出 0~3 号弦范围。",
                    "The string index falls outside 0 – 3, reported as {N} 弦超出 0~3 号弦范围 / string {N} is outside the 0-3 range.",
                  ),
                  l(
                    "正常生成不该出现，看到它就说明数据异常，值得反馈。",
                    "This should never happen in a healthy run — seeing it means something is wrong with the data, worth reporting.",
                  ),
                ],
                [
                  l("品格超限", "品格超限 / Fret limit"),
                  l(
                    "某个音超出了该弦的品格上限，提示形如 {N} 弦 {M} 品超出该弦品格上限 {上限}。",
                    "A note goes past that string's fret limit, reported as {N} 弦 {M} 品超出该弦品格上限 {上限} / string {N} fret {M} exceeds the fret limit {上限} for that string.",
                  ),
                  l(
                    "上限来自角色的 .violinist。内置小提琴是 0 弦 19 品、1 弦 14 品、2 弦 12 品、3 弦 10 品；大提琴除 0 弦外最多到 9 品。",
                    "The limit comes from the performer's .violinist. The built-in violin allows 19 frets on string 0, 14 on string 1, 12 on string 2 and 10 on string 3; the cello allows at most 9 frets on strings 1 – 3.",
                  ),
                ],
                [
                  l("空弦品号", "空弦品号 / Open string fret"),
                  l(
                    "被判定为空弦的音，品号却不是 0，提示形如 {N} 弦是空弦音，但品号是 {M}。",
                    "A note treated as an open string carries a non-zero fret, reported as {N} 弦是空弦音，但品号是 {M} / string {N} is an open string but its fret is {M}.",
                  ),
                  l(
                    "属于数据自相矛盾，一般说明生成阶段出了问题。",
                    "An internal contradiction, usually a sign something went wrong during generation.",
                  ),
                ],
                [
                  l("同帧跨度", "同帧跨度 / Frame span"),
                  l(
                    "同一帧里同时按的音跨越 6 品以上，提示形如 同一帧跨越 {N} 品（{低} → {高} 品），需要换把才能按到。",
                    "Notes pressed on the same frame span six or more frets, reported as 同一帧跨越 {N} 品（{低} → {高} 品），需要换把才能按到 / one frame spans {N} frets ({低} → {高}), which needs a position shift.",
                  ),
                  l(
                    "判定阈值是跨度大于 5 品；这是双音/和弦里最容易露馅的地方。",
                    "The threshold is a span greater than 5 frets — the most common giveaway in double stops and chords.",
                  ),
                ],
                [
                  l("手指顺序", "手指顺序 / Finger order"),
                  l(
                    "低手指按了更高的品，提示形如 {弦} 弦 {品} 品用 {指} 指，却比 {弦} 弦 {品} 品的 {指} 指还低。",
                    "A lower finger stops a higher fret, reported along the lines of {弦} 弦 {品} 品用 {指} 指，却比 {弦} 弦 {品} 品的 {指} 指还低 / string {弦} fret {品} uses finger {指}, but string {弦} fret {品} uses finger {指}.",
                  ),
                  l(
                    "手型在物理上讲不通，通常和 同帧跨度 一起出现。",
                    "The hand shape is physically impossible; it usually shows up together with 同帧跨度 / Frame span.",
                  ),
                ],
                [
                  l("换把", "换把 / Shift"),
                  l(
                    "相邻两个发声时刻间隔不到 0.25 秒、手位却移动了 4 品以上，提示形如 {N} 品的手位变化只隔了 {秒} 秒，来不及换把。",
                    "Two consecutive sounding moments less than 0.25 s apart where the hand position moves more than 4 frets, reported as {N} 品的手位变化只隔了 {秒} 秒，来不及换把 / a {N} fret hand-position change within {秒}s leaves no time to shift.",
                  ),
                  l(
                    "这类提示最实用：它直接指出「手来不及」的段落，可以据此调整量化或干脆修正 MIDI。",
                    "The most actionable category: it points straight at passages where the hand cannot keep up, which you can fix with quantization or by editing the MIDI.",
                  ),
                ],
                [
                  l("休止", "休止 / Rest"),
                  l(
                    "音量记为 0 的帧上仍然有按弦音，提示 休止帧上仍有按弦音。",
                    "A frame recorded at zero volume still carries a stopped note: 休止帧上仍有按弦音 / a rest frame still carries a stopped note.",
                  ),
                  l(
                    "级别是提醒而不是警告，多半是原曲力度写法的取舍，可以忽略。",
                    "This one is a notice rather than a warning and is usually just how the source dynamics were written — safe to ignore.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "info",
              title: l("提示不等于错误", "A flag is not a failure"),
              text: l(
                "校验只负责把可疑的地方指出来，不会阻止生成，也不改数据。高把位、跨弦双音这些刻意的写法本来就容易被报；真正需要处理的是 弦号、空弦品号 这类自相矛盾的条目。",
                "The check only points at suspicious spots; it never blocks generation or changes data. Deliberate choices like high positions and cross-string double stops get flagged by design. The entries that really need attention are the self-contradictory ones such as 弦号 / String index and 空弦品号 / Open string fret.",
              ),
            },
          ],
        },
      ],
    },

    // ═════════════════════════════════════════════════════════════
    // 默认值速查
    // ═════════════════════════════════════════════════════════════
    {
      id: "defaults",
      title: l("「什么都不用改」速查表", "The “leave it alone” cheat sheet"),
      blocks: [
        {
          kind: "p",
          text: l(
            "第一次用这套软件，除了选角色、选 MIDI、勾轨道，其余全部保持默认就能出成品。下表列出默认值，以及各自「什么时候才值得动」。",
            "On a first run — beyond picking a performer, a MIDI and some tracks — everything can stay at its default and you will still get a usable result. Below are the defaults and the only situations worth changing them for.",
          ),
        },
        {
          kind: "table",
          head: [
            l("参数", "Parameter"),
            l("默认值", "Default"),
            l("什么时候才需要动它", "When it is worth changing"),
          ],
          rows: [
            [
              l("FPS", "FPS"),
              l("60", "60"),
              l(
                "插件工程的帧率不是 60 帧时。",
                "When your plugin project does not run at 60 fps.",
              ),
            ],
            [
              l("更高八度", "更高八度 / Higher octave"),
              l("不勾选", "Off"),
              l(
                "旋律整体偏高、高把位挤成一团时勾上试试。",
                "Try it when a high-register melody crowds into the upper positions.",
              ),
            ],
            [
              l("弓速", "弓速 / Bow speed"),
              l("1.2 位置/秒", "1.2 位置/秒 / positions/sec"),
              l(
                "长音想要更宽的长弓，或者快句里弓来不及换向。",
                "Broader strokes for long notes, or the bow failing to keep up in fast passages.",
              ),
            ],
            [
              l(
                "揉弦速度 / 揉弦幅度",
                "揉弦速度 / Vibrato speed / 揉弦幅度 / Vibrato amplitude",
              ),
              l("各 1.0 倍率", "1.0 倍率 / Multiplier each"),
              l(
                "觉得揉弦太死板或太夸张时，先调速度再调幅度。",
                "When vibrato feels stiff or overdone — rate first, then depth.",
              ),
            ],
            [
              l("运弓策略", "运弓策略 / Bow Strategy"),
              l("长弓模式", "长弓模式 / Long bow"),
              l(
                "需要每个音都明确换弓向的段落，改 强制换弓模式。",
                "For passages that need an explicit bow change on every note, pick 强制换弓模式 / Force bow change.",
              ),
            ],
            [
              l("调弦方案:", "调弦方案: / Tuning scheme"),
              l(
                "跟随角色的 .violinist",
                "Whatever the performer's .violinist says",
              ),
              l(
                "同一段音乐在高把位来回挣扎时，换更低的定弦试试。",
                "When a passage keeps fighting in high positions, try a lower tuning.",
              ),
            ],
            [
              l("使用通道", "使用通道 / Use Channel"),
              l("-1（不过滤）", "-1 (no filter)"),
              l(
                "文件里混了伴奏/打击乐通道，只想取其中一条时。",
                "When the file mixes in accompaniment or percussion and you want only one channel.",
              ),
            ],
            [
              l("音符量化", "音符量化 / Note Quantization"),
              l("1/16", "1/16"),
              l(
                "齐奏被拆得太碎，或者反过来想保留细腻的时间差。",
                "Unisons chopped too finely — or, the other way, when you want to keep fine timing.",
              ),
            ],
            [
              l("轨道列里的 拨奏", "拨奏 / Pizzicato in the track list"),
              l("不勾选", "Off"),
              l(
                "该轨道确实是拨弦段落、右手不该运弓时。",
                "When the track really is plucked and the right hand should not bow.",
              ),
            ],
            [
              l(
                "弦乐分轨（四重奏） / 修剪 MIDI 音符 / 速度均一化处理",
                "弦乐分轨（四重奏） / Split String Quartet / 修剪 MIDI 音符 / Trim MIDI Notes / 速度均一化处理 / Normalize Velocity",
              ),
              l("不点", "Not used"),
              l(
                "需要四人编制、音符互相压着、或者力度悬殊到弓法不自然时，再拿它们做预处理。",
                "Reach for these only when you want a quartet arrangement, notes are piling on top of each other, or velocity swings make the bowing look unnatural.",
              ),
            ],
          ],
        },
      ],
    },

    // ═════════════════════════════════════════════════════════════
    // 常见疑问
    // ═════════════════════════════════════════════════════════════
    {
      id: "faq",
      title: l("参数相关的常见疑问", "Parameter questions we get"),
      blocks: [
        {
          kind: "callout",
          tone: "info",
          title: l(
            "改了 FPS 要不要全部重跑？",
            "Do I have to re-run everything after changing FPS?",
          ),
          text: l(
            "要。FPS 同时参与指法与动画两个阶段，只重跑 2. 生成动画 会让两边的帧号对不上。改 FPS 或 更高八度 都建议从 1. 生成指法 重跑（或直接 一键执行）。",
            "Yes. FPS is used in both the fingering and animation stages, so re-running only 2. 生成动画 / 2. Generate Animation leaves the frame numbers inconsistent. After changing FPS or 更高八度 / Higher octave, re-run from 1. 生成指法 / 1. Generate Fingering (or just 一键执行 / Run All).",
          ),
        },
        {
          kind: "callout",
          tone: "warn",
          title: l(
            "更高八度 勾上以后听起来更低，是不是标反了？",
            "更高八度 / Higher octave sounds lower — is the label inverted?",
          ),
          text: l(
            "勾上以后解析时是把音符整体**下移**一个八度（源码里那个参数叫 octave_down_checkbox，做的是 - 12），界面文字与它的实际方向确实相反。以听感为准：勾上 = 整体降八度。",
            "Ticking it shifts every note **down** an octave while parsing — the source flag is literally named octave_down_checkbox and applies - 12. So the label and the behaviour really do point opposite ways. Trust your ears: ticked means one octave lower.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l(
            "弓速 调到最大，为什么个别音还是走得很快？",
            "弓速 / Bow speed is maxed out but some notes still move fast",
          ),
          text: l(
            "弓速 是基准值，实际速度还会按音符力度和所在弦的权重缩放，力度大的音天然更快。它调的是整体手感，不是硬上限。",
            "弓速 / Bow speed is a baseline: the actual speed is scaled by note velocity and the string's weight, so loud notes are naturally faster. It shapes the overall feel rather than setting a hard ceiling.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l(
            "音符量化 选 1/16 会不会把装饰音吃掉？",
            "Will 1/16 quantization swallow grace notes?",
          ),
          text: l(
            "只有开始时间落在同一个量化窗口里的音才会被合并成一组，窗口之外原样保留。想一个不漏就选 无量化，代价是同一帧的音变少、弓法更碎。",
            "Only notes whose start times fall inside the same quantization window get merged; everything else is kept as is. Pick 无量化 / None to lose nothing — at the cost of fewer shared frames and busier bowing.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l(
            "弦乐分轨（四重奏） 的文件在哪里？",
            "Where does 弦乐分轨（四重奏） / Split String Quartet put its file?",
          ),
          text: l(
            "在源 MIDI 同一个目录下，文件名是 {原名}_quartet.mid。要拿它生成动画，得回 01 MIDI 文件 把这份新文件选进来、重新扫描、重新 添加选中的轨道。",
            "Next to the source MIDI, named {原名}_quartet.mid. To generate from it, go back to 01 MIDI 文件 / MIDI File, load that new file, scan again and click 添加选中的轨道 / Add Selected Tracks again.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l(
            "🎼 指法检查 一直说 找不到指法文件？",
            "The 🎼 指法检查 / 🎼 Fingering tab keeps saying 找不到指法文件 / No fingering file found",
          ),
          text: l(
            "这一屏读的是 output/left_hand_recorder/{MIDI}_{轨道}.json，先回 ▶ 执行操作 点 1. 生成指法。注意文件名里带轨道号：如果生成时用的轨道组合和现在选的不一样，这里就找不到对应文件，改用下拉菜单里列出的那些即可。",
            "The tab reads output/left_hand_recorder/{MIDI}_{轨道}.json, so run 1. 生成指法 / 1. Generate Fingering on the ▶ 执行操作 / ▶ Execute tab first. Note that the file name carries the track numbers: if the tracks used for generation differ from the ones selected now, there is no matching file — just pick one from the dropdown instead.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l(
            "校验提示 有二十几条，是生成错了吗？",
            "Twenty-odd 校验提示 / checks — did generation go wrong?",
          ),
          text: l(
            "多数不是。高把位、跨弦双音、快速换把这些刻意写法都会被报出来，休止 那一类更是只算提醒。只有 弦号、空弦品号 这类自相矛盾的条目值得回头查数据。",
            "Usually not. Deliberate writing — high positions, cross-string double stops, quick position shifts — gets flagged by design, and the 休止 / Rest category is only a notice. Only self-contradictory entries such as 弦号 / String index and 空弦品号 / Open string fret are worth going back to the data for.",
          ),
        },
      ],
    },
  ],
};
